import {useState, useCallback} from 'react';
import {useDatabase} from '@nozbe/watermelondb/react';
import {fetchSessionsByDate} from 'src/providers/watermelondb/sync/fetch-sessions-by-date';

interface UseSessionDateSyncResult {
  isLoading: boolean;
  error: Error | null;
  syncDateRange: (startDate: Date, endDate: Date) => Promise<void>;
}

/**
 * Hook to manage on-demand fetching of sessions for specific date ranges
 *
 * Usage:
 * ```
 * const {isLoading, syncDateRange} = useSessionDateSync();
 *
 * useEffect(() => {
 *   syncDateRange(filters.startDate, filters.endDate);
 * }, [filters.startDate, filters.endDate]);
 * ```
 */
export const useSessionDateSync = (): UseSessionDateSyncResult => {
  const database = useDatabase();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const syncDateRange = useCallback(
    async (startDate: Date, endDate: Date) => {
      try {
        // Start fetching
        setIsLoading(true);
        setError(null);

        await fetchSessionsByDate({
          database,
          startDate,
          endDate,
        });

        // Success
        setError(null);
      } catch (err) {
        console.error('Error syncing session date range:', err);
        setError(
          err instanceof Error
            ? err
            : new Error('Failed to sync session date range'),
        );
      } finally {
        setIsLoading(false);
      }
    },
    [database],
  );

  return {
    isLoading,
    error,
    syncDateRange,
  };
};
